import { TagVariant } from '../components/tag/index';
import { Locale, StringKey } from '../lang/index';
import { AuthProvider } from './auth.types';

export type PatchUserDto = {
  email?: string;
  fullName?: string;
  language?: Locale;
  password?: string;
};

export type PatchUserNotificationSettings = {
  adminFundraising?: boolean;
  adminInvestment?: boolean;
  adminTransaction?: boolean;
  adminVesting?: boolean;
  companyCreation?: boolean;
  enterprisePlanRequest?: boolean;
  exerciseDone?: boolean;
  exerciseRequest?: boolean;
  exerciseRequestConfirmation?: boolean;
  fundraising?: boolean;
  grant?: boolean;
  investment?: boolean;
  investmentConversions?: boolean;
  invitation?: boolean;
  passwordReset?: boolean;
  plan?: boolean;
  planUpgrade?: boolean;
  pool?: boolean;
  receiptsConfirmations?: boolean;
  roleChange?: boolean;
  shareClass?: boolean;
  stakeholderLimit?: boolean;
  transaction?: boolean;
  verificationLink?: boolean;
  vesting?: boolean;
};

export enum Importance {
  HIGH = 'high',
  LOW = 'low',
  MEDIUM = 'medium',
}

export const importanceColor: Record<Importance, TagVariant> = {
  [Importance.HIGH]: TagVariant.YELLOW,
  [Importance.LOW]: TagVariant.GRAY,
  [Importance.MEDIUM]: TagVariant.GREEN,
};

export const importanceTitle: Record<Importance, StringKey> = {
  [Importance.HIGH]: 'HIGH',
  [Importance.LOW]: 'LOW',
  [Importance.MEDIUM]: 'MEDIUM',
};

export enum AdminNotificationTag {
  ADMIN_FUNDRAISING = 'admin-fundraising',
  ADMIN_INVESTMENT = 'admin-investment',
  ADMIN_TRANSACTION = 'admin-transaction',
  ADMIN_VESTING = 'admin-vesting',
  EXERCISE_REQUEST = 'exercise-request',
  PLAN = 'plan',
  POOL = 'pool',
  RECEIPTS_CONFIRMATIONS = 'receipts-confirmations',
  SHARE_CLASS = 'share-class',
}
export enum NotificationTag {
  EXERCISE_DONE = 'exercise-done',
  FUNDRAISING = 'fundraising',
  GRANT = 'grant',
  INVESTMENT = 'investment',
  INVESTMENT_CONVERSIONS = 'investment-conversions',
  TRANSACTION = 'transaction',
  VESTING = 'vesting',
}

type NotificationTagUnion = AdminNotificationTag | NotificationTag;

export const notificationTagImportance: Record<NotificationTagUnion, Importance> = {
  [NotificationTag.EXERCISE_DONE]: Importance.MEDIUM,
  [NotificationTag.GRANT]: Importance.HIGH,
  [NotificationTag.VESTING]: Importance.MEDIUM,
  [NotificationTag.TRANSACTION]: Importance.HIGH,
  [NotificationTag.INVESTMENT]: Importance.HIGH,
  [NotificationTag.INVESTMENT_CONVERSIONS]: Importance.MEDIUM,
  [NotificationTag.FUNDRAISING]: Importance.HIGH,
  [AdminNotificationTag.EXERCISE_REQUEST]: Importance.HIGH,
  [AdminNotificationTag.POOL]: Importance.MEDIUM,
  [AdminNotificationTag.PLAN]: Importance.MEDIUM,
  [AdminNotificationTag.ADMIN_VESTING]: Importance.MEDIUM,
  [AdminNotificationTag.ADMIN_TRANSACTION]: Importance.HIGH,
  [AdminNotificationTag.SHARE_CLASS]: Importance.HIGH,
  [AdminNotificationTag.ADMIN_INVESTMENT]: Importance.HIGH,
  [AdminNotificationTag.ADMIN_FUNDRAISING]: Importance.HIGH,
  [AdminNotificationTag.RECEIPTS_CONFIRMATIONS]: Importance.HIGH,
};

export const notificationTagTitle: Record<NotificationTagUnion, StringKey> = {
  [NotificationTag.EXERCISE_DONE]: 'EXERCISE_UPDATES',
  [NotificationTag.GRANT]: 'GRANT_UPDATES',
  [NotificationTag.VESTING]: 'VESTING_UPDATES',
  [NotificationTag.TRANSACTION]: 'TRANSACTIONS_UPDATES',
  [NotificationTag.INVESTMENT]: 'INVESTMENTS_UPDATES',
  [NotificationTag.INVESTMENT_CONVERSIONS]: 'INVESTMENT_CONVERSIONS',
  [NotificationTag.FUNDRAISING]: 'FUNDRAISING_UPDATES',
  [AdminNotificationTag.EXERCISE_REQUEST]: 'EXERCISE_REQUESTS',
  [AdminNotificationTag.POOL]: 'POOL_UPDATES',
  [AdminNotificationTag.PLAN]: 'PLAN_UPDATES',
  [AdminNotificationTag.ADMIN_VESTING]: 'VESTING_UPDATES',
  [AdminNotificationTag.ADMIN_TRANSACTION]: 'TRANSACTIONS_UPDATES',
  [AdminNotificationTag.SHARE_CLASS]: 'SHARE_CLASS_UPDATES',
  [AdminNotificationTag.ADMIN_INVESTMENT]: 'INVESTMENT_UPDATES',
  [AdminNotificationTag.ADMIN_FUNDRAISING]: 'FUNDRAISING_PROFILE',
  [AdminNotificationTag.RECEIPTS_CONFIRMATIONS]: 'RECEIPTS_CONFIRMATIONS_SUBSCRIPTION_PAYMENT',
};

export const notificationTagDescription: Record<NotificationTagUnion, StringKey> = {
  [NotificationTag.EXERCISE_DONE]: 'STAY_INFORMED_ABOUT_EXERCISED_GRANTS',
  [NotificationTag.GRANT]: 'GET_ALERTS_ON_NEWLY_ISSUED_EQUITY_GRANTS',
  [NotificationTag.VESTING]: 'GET_ALERTED_ABOUT_VESTING_MILESTONES',
  [NotificationTag.TRANSACTION]: 'STAY_INFORMED_ABOUT_SHARE_TRANSACTIONS',
  [NotificationTag.INVESTMENT]: 'TRACK_NEW_OR_UPDATED_INVESTMENTS',
  [NotificationTag.INVESTMENT_CONVERSIONS]: 'LEARN_WHEN_SAFES_CONVERT',
  [NotificationTag.FUNDRAISING]: 'KEEP_AN_EYE_ON_NEW_FUNDRAISING_ROUNDS',
  [AdminNotificationTag.EXERCISE_REQUEST]: 'NOTIFICATIONS_ABOUT_EXERCISE_REQUESTS',
  [AdminNotificationTag.POOL]: 'ALERTS_FOR_CHANGES_TO_EQUITY_POOL',
  [AdminNotificationTag.PLAN]: 'NOTIFICATIONS_REGARDING_CHANGES_TO_EQUITY_PLANS',
  [AdminNotificationTag.ADMIN_VESTING]: 'UPDATES_ON_VESTING_SCHEDULES',
  [AdminNotificationTag.ADMIN_TRANSACTION]: 'UPDATES_ON_NEW_RECORDED_TRANSACTIONS',
  [AdminNotificationTag.SHARE_CLASS]: 'GET_NOTIFIED_ABOUT_SHARE_CLASSES',
  [AdminNotificationTag.ADMIN_INVESTMENT]: 'KEEP_TRACK_OF_SAFES_CONVERTIBLES_LOANS',
  [AdminNotificationTag.ADMIN_FUNDRAISING]: 'ALERTS_FOR_FUNDRAISING_ROUNDS',
  [AdminNotificationTag.RECEIPTS_CONFIRMATIONS]: 'RECEIPTS_CONFIRMATIONS_SUBSCRIPTION_PAYMENT',
};

export const notificationTagToUserNotificationSettings: Record<
  AdminNotificationTag | NotificationTag,
  keyof Except<EmailSettings, 'id'>
> = {
  [AdminNotificationTag.EXERCISE_REQUEST]: 'exerciseRequest',
  [AdminNotificationTag.POOL]: 'pool',
  [AdminNotificationTag.PLAN]: 'plan',
  [AdminNotificationTag.ADMIN_VESTING]: 'adminVesting',
  [AdminNotificationTag.ADMIN_TRANSACTION]: 'adminTransaction',
  [AdminNotificationTag.SHARE_CLASS]: 'shareClass',
  [AdminNotificationTag.ADMIN_INVESTMENT]: 'adminInvestment',
  [AdminNotificationTag.ADMIN_FUNDRAISING]: 'adminFundraising',
  [AdminNotificationTag.RECEIPTS_CONFIRMATIONS]: 'receiptsConfirmations',
  [NotificationTag.EXERCISE_DONE]: 'exerciseDone',
  [NotificationTag.GRANT]: 'grant',
  [NotificationTag.VESTING]: 'vesting',
  [NotificationTag.TRANSACTION]: 'transaction',
  [NotificationTag.INVESTMENT]: 'investment',
  [NotificationTag.FUNDRAISING]: 'fundraising',
  [NotificationTag.INVESTMENT_CONVERSIONS]: 'investmentConversions',
};

export const notificationTagMap = Object.values(NotificationTag);
export const adminNotificationTagMap = Object.values(AdminNotificationTag);

export type EmailSettings = {
  adminFundraising: boolean;
  adminInvestment: boolean;
  adminTransaction: boolean;
  adminVesting: boolean;
  companyCreation: boolean;
  enterprisePlanRequest: boolean;
  exerciseDone: boolean;
  exerciseRequest: boolean;
  exerciseRequestConfirmation: boolean;
  fundraising: boolean;
  grant: boolean;
  id: string;
  investment: boolean;
  investmentConversions: boolean;
  invitation: boolean;
  passwordReset: boolean;
  plan: boolean;
  planUpgrade: boolean;
  pool: boolean;
  receiptsConfirmations: boolean;
  roleChange: boolean;
  shareClass: boolean;
  stakeholderLimit: boolean;
  transaction: boolean;
  verificationLink: boolean;
  vesting: boolean;
};

export type InAppSettings = {
  adminFundraising: boolean;
  adminInvestment: boolean;
  adminTransaction: boolean;
  adminVesting: boolean;
  companyCreation: boolean;
  enterprisePlanRequest: boolean;
  exerciseDone: boolean;
  exerciseRequest: boolean;
  exerciseRequestConfirmation: boolean;
  fundraising: boolean;
  grant: boolean;
  id: string;
  investment: boolean;
  investmentConversions: boolean;
  invitation: boolean;
  passwordReset: boolean;
  plan: boolean;
  planUpgrade: boolean;
  pool: boolean;
  receiptsConfirmations: boolean;
  roleChange: boolean;
  shareClass: boolean;
  stakeholderLimit: boolean;
  transaction: boolean;
  verificationLink: boolean;
  vesting: boolean;
};

export type User = {
  adminEmailAppNotifications: undefined;
  adminInAppNotifications: undefined;
  authProvider: AuthProvider;
  createdAt: string;
  email: string;
  emailSettings: EmailSettings;
  fullName: string;
  id: string;
  inAppSettings: InAppSettings;
  language: Locale;
  selectedCompany?: string;
  stakeholderEmailAppNotifications: undefined;
  stakeholderInAppNotifications: undefined;
  stripeCustomerId: string;
};
